import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import SEO from "components/SEO.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";
import { SectionHeading } from "components/misc/Headings";
import mythVsRealityImg1 from "assets/myvs_reality_img1.png";
import mythVsRealityImg2 from "assets/myth_vs_reality_img2.png";
import poshMythsVsRealityImg from "images/postMythsVsReality.jpeg";

const HeadingRow = tw.div`flex justify-center`;
const Heading = tw(SectionHeading)`text-gray-900 mb-10 text-center`;

const IntroSection = tw.div`mb-12`;
const IntroImage = tw.img`w-full max-w-2xl mx-auto rounded-lg shadow-lg mb-8`;
const IntroText = tw.p`text-lg text-gray-700 leading-relaxed mb-6`;

const MythSection = tw.div`mb-12 bg-gray-100 rounded-lg p-8`;
const MythTitle = tw.h2`text-2xl font-bold text-red-600 mb-4 flex items-center`;
const RealityTitle = tw.h2`text-2xl font-bold text-green-600 mb-4 flex items-center`;
const MythIcon = tw.span`mr-3 text-3xl`;
const MythContent = tw.div`text-gray-700 leading-relaxed`;
const MythImage = tw.img`w-full max-w-md mx-auto rounded-lg shadow-md my-6`;

const AtGlanceSection = tw.div`mb-12 rounded-lg p-8`;
const AtGlanceTitle = tw.h2`text-2xl font-bold text-gray-900 mb-4 flex items-center`;

const FlexContainer = styled.div`
  ${tw`flex items-center mb-8`}
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;
const ImageContainer = styled.div`
  ${tw`flex-shrink-0`}
  
  @media (max-width: 768px) {
    width: 100%;
    order: 1;
  }
`;
const ContentContainer = styled.div`
  ${tw`flex-1`}
  
  @media (max-width: 768px) {
    width: 100%;
    order: 2;
  }
`;

const ConclusionSection = styled.div`
  ${tw`mt-16 p-8 rounded-lg`}
  background-color: #fffdf6;
`;
const ConclusionTitle = tw.h2`text-3xl font-bold text-black mb-6`;
const ConclusionText = tw.p`text-lg text-black leading-relaxed mb-4`;

const Text = styled.div`
  ${tw`text-lg text-gray-800`}
  p {
    ${tw`mt-2 leading-loose`}
  }
  h1 {
    ${tw`text-3xl font-bold mt-10`}
  }
  h2 {
    ${tw`text-2xl font-bold mt-8`}
  }
  h3 {
    ${tw`text-xl font-bold mt-6`}
  }
  ul {
    ${tw`list-disc list-inside ml-6`}
    li {
      ${tw`mb-3 text-gray-700`}
      p {
        ${tw`mt-0 inline leading-normal`}
      }
    }
  }
  strong {
    ${tw`font-semibold text-gray-900`}
  }
`;
export default ({ headingText = "POSH Myths vs Facts" }) => {
    return (
        <AnimationRevealPage>
            <SEO
                title="POSH Myths vs Facts - SEQUILERN"
                description="Separating myths from facts about the POSH Act (Sexual Harassment of Women at Workplace Act). Learn about workplace safety, compliance requirements, and your rights."
                keywords="POSH Act, sexual harassment, workplace safety, myths vs facts, compliance, women safety, workplace rights"
                url="https://www.sequilern.com/posh-myths-vs-reality"
            />
            <Header />
            <Container>
                <ContentWithPaddingXl>
                    <HeadingRow>
                        <Heading>{headingText}</Heading>
                    </HeadingRow>
                    
                    {/* Introduction Section */}
                    <IntroSection>
                        <IntroImage src={mythVsRealityImg1} alt="POSH Act Overview" />
                        <IntroText>
                            The Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 
                            2013—commonly known as the POSH Act—was enacted following the Vishakha vs. State of 
                            Rajasthan judgment of the Hon'ble Supreme Court. Before this, India had no dedicated law 
                            governing workplace sexual harassment or laying down specific procedures for redressal.
                        </IntroText>
                        <IntroText>
                            Consequently, many incidents were ignored or suppressed, leading to unsafe work 
                            environments. The POSH Act is therefore a landmark legislation, designed to ensure workplace 
                            safety, dignity, and equality. However, its wide application has also led to several myths 
                            and misconceptions about its scope and enforcement. Let's separate myth from reality:
                        </IntroText>
                    </IntroSection>

                    {/* Myth 1 */}
                    <MythSection>
                        <MythTitle>
                            <MythIcon>❌</MythIcon>
                            Myth 1: Only large corporations are covered under the POSH Act
                        </MythTitle>
                        <RealityTitle>
                            <MythIcon>✅</MythIcon>
                            Reality:
                        </RealityTitle>
                        <Text>
                            <p>The POSH Act applies to all workplaces, irrespective of size, type, or sector—including government offices, private companies, schools, hospitals, NGOs, and even the unorganised sector.</p>
                            <ul>
                                <li>Workplaces with 10 or more employees must constitute an Internal Committee (IC) under Section 4 of the Act.</li>
                                <li>For smaller organisations, complaints are handled by a Local Committee constituted by the District Officer.</li>
                            </ul>
                            <p><strong>Thus, every workplace, big or small, falls under the ambit of the Act.</strong></p>
                        </Text>
                    </MythSection>

                    {/* Myth 2 */}
                    <MythSection>
                        <MythTitle>
                            <MythIcon>❌</MythIcon>
                            Myth 2: The Internal Committee cannot act without a written complaint
                        </MythTitle>
                        <RealityTitle>
                            <MythIcon>✅</MythIcon>
                            Reality:
                        </RealityTitle>
                        <Text>
                            <p>While a written complaint is part of the formal process, the Act is sensitive to the fact that victims may find it difficult to draft one.</p>
                            <ul>
                                <li>The IC is duty-bound to assist the complainant in preparing the written complaint.</li>
                                <li>The IC can also initiate an inquiry suo motu if it observes misconduct or receives credible information.</li>
                            </ul>
                            <p><strong>Therefore, the absence of a written complaint does not mean inaction.</strong></p>
                        </Text>
                    </MythSection>

                    {/* Myth 3 with Image */}
                    <MythSection>
                        <MythTitle>
                            <MythIcon>❌</MythIcon>
                            Myth 3: False complaints are frequent and never punished
                        </MythTitle>
                        <FlexContainer>
                            <ImageContainer>
                                <MythImage src={mythVsRealityImg2} alt="POSH Act Myth 3 Illustration" />
                            </ImageContainer>
                            <ContentContainer>
                                <RealityTitle>
                                    <MythIcon>✅</MythIcon>
                                    Reality:
                                </RealityTitle>
                                <Text>
                                    <p>Studies and experience show that false complaints are rare. Still, the Act has safeguards against misuse.</p>
                                    <ul>
                                        <li>Under Section 14, if a complaint is found to be malicious, the IC/Local Committee can recommend action against the complainant.</li>
                                        <li>However, lack of evidence does not automatically mean the complaint is false.</li>
                                    </ul>
                                    <p><strong>Committees must carefully distinguish between unsubstantiated and malicious complaints.</strong></p>
                                </Text>
                            </ContentContainer>
                        </FlexContainer>
                    </MythSection>

                    {/* Myth 4 */}
                    <MythSection>
                        <MythTitle>
                            <MythIcon>❌</MythIcon>
                            Myth 4: POSH does not apply to Work From Home (WFH)
                        </MythTitle>
                        <RealityTitle>
                            <MythIcon>✅</MythIcon>
                            Reality:
                        </RealityTitle>
                        <Text>
                            <p>The Act explicitly recognizes that "workplace" includes any place connected to work, whether physical or virtual.</p>
                            <ul>
                                <li>Online platforms like Zoom, MS Teams, Google Meet, emails, or messaging apps fall under its purview.</li>
                                <li>Harassment over video calls, chat messages, or even during client interactions while working remotely is covered.</li>
                            </ul>
                            <p><strong>In today's digital-first world, the law ensures protection beyond office walls.</strong></p>
                        </Text>
                    </MythSection>

                    {/* Myth 5 */}
                    <MythSection>
                        <MythTitle>
                            <MythIcon>❌</MythIcon>
                            Myth 5: Only physical harassment counts
                        </MythTitle>
                        <RealityTitle>
                            <MythIcon>✅</MythIcon>
                            Reality:
                        </RealityTitle>
                        <Text>
                            <p>The POSH Act defines sexual harassment broadly under Section 2(n), covering:</p>
                            <ul>
                                <li>Demands or requests for sexual favours</li>
                                <li>Sexually coloured remarks</li>
                                <li>Showing pornography</li>
                                <li>Unwelcome physical, verbal, or non-verbal conduct of a sexual nature</li>
                            </ul>
                            <p>This includes inappropriate jokes, repeated advances, offensive content, intrusive questions, or suggestive gestures. <strong>Harassment is not limited to physical contact.</strong></p>
                        </Text>
                    </MythSection>

                    {/* Conclusion */}
                    <ConclusionSection>
                        <ConclusionTitle>Building Safer, More Inclusive Workplaces</ConclusionTitle>
                        <ConclusionText>
                            The POSH Act is a crucial step towards building safer, more inclusive workplaces. 
                            Unfortunately, myths often dilute its purpose and discourage people from seeking redressal. 
                            Awareness, training, and open dialogue are key to dispelling these misconceptions.
                        </ConclusionText>
                        <ConclusionText>
                            Employers must not only comply with the Act but also actively promote sensitisation through 
                            seminars and awareness campaigns. Employees, in turn, should be informed about their rights 
                            and remedies.
                        </ConclusionText>
                        <ConclusionText>
                            <strong>When understood and implemented correctly, the POSH Act becomes a powerful safeguard—not 
                            just a compliance requirement, but a commitment to dignity and equality at the workplace.</strong>
                        </ConclusionText>
                    </ConclusionSection>

                    {/* At a Glance Section */}
                    <AtGlanceSection>
                        <AtGlanceTitle>
                            <MythIcon>📋</MythIcon>
                            At a Glance
                        </AtGlanceTitle>
                        <MythImage src={poshMythsVsRealityImg} alt="POSH Myths vs Reality At a Glance" />
                        {/* <Text>
                            <p>
                                <strong>The POSH Act is comprehensive legislation that applies universally across all workplaces, 
                                regardless of size or sector. It provides robust mechanisms for prevention, complaint handling, 
                                and redressal while maintaining safeguards against misuse.</strong>
                            </p>
                        </Text> */}
                    </AtGlanceSection>
                </ContentWithPaddingXl>
            </Container>
            <Footer />
        </AnimationRevealPage>
    );
};

